import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  List,
  ListItem,
  Card,
  CardContent,
  Switch,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';

const MenuItems = ({ unitCode, companyCode, unitType, companyName, isPureVeg, userId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuItems, setMenuItems] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAssignedItems = () => {
    if (!unitCode || !companyCode || !userId) {
      setError('Missing unit code, company code, or user ID. Please ensure you are logged in correctly.');
      console.error('MenuItems: Missing required data', { unitCode, companyCode, userId, query: location?.search });
      setIsLoading(false);
      return () => {};
    }

    try {
      console.log('MenuItems: Fetching assigned menu items from companymenu', { unitCode, companyCode, userId });
      const menuQuery = query(
        collection(db, 'companymenu'),
        where('unitCode', '==', unitCode),
        where('companyCode', '==', companyCode),
        where('staff', '==', userId)
      );

      const unsubscribe = onSnapshot(menuQuery, (snapshot) => {
        try {
          const items = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            ingredients: Array.isArray(doc.data().ingredients) ? doc.data().ingredients.join(', ') : doc.data().ingredients || 'No ingredients listed',
            description: doc.data().description || 'No description available',
            imageUrl: doc.data().imageUrl || '/placeholder.png',
            isAvailable: doc.data().isAvailable !== false,
          }));
          console.log('MenuItems: Assigned menu items', { itemIds: items.map(item => item.id), unitCode, companyCode, userId, documentCount: snapshot.size });
          setMenuItems(items);
          setError(items.length === 0 ? 'No menu items assigned to you for this unit.' : null);
          setIsLoading(false);
        } catch (err) {
          console.error('MenuItems: Error processing snapshot', { err, unitCode, companyCode, userId });
          setError(`Failed to load menu items: ${err.message}`);
          setMenuItems([]);
          setIsLoading(false);
        }
      }, (err) => {
        console.error('MenuItems: Snapshot listener error', { err, unitCode, companyCode, userId });
        setError(`Failed to listen for menu item updates: ${err.message}`);
        setMenuItems([]);
        setIsLoading(false);
      });

      return unsubscribe;
    } catch (err) {
      console.error('MenuItems: Error setting up snapshot listener', { err, unitCode, companyCode, userId });
      setError(`Failed to load assigned menu items: ${err.message}`);
      setMenuItems([]);
      setIsLoading(false);
      return () => {};
    }
  };

  useEffect(() => {
    console.log('MenuItems: Received props', { unitCode, companyCode, unitType, companyName, isPureVeg, userId });
    if (!unitCode || !companyCode || !userId) {
      setError('Missing unit code, company code, or user ID. Please ensure you are logged in correctly.');
      console.error('MenuItems: Missing required data', { unitCode, companyCode, userId });
      setIsLoading(false);
      return;
    }

    sessionStorage.setItem('unitCode', unitCode);
    sessionStorage.setItem('companyCode', companyCode);
    sessionStorage.setItem('unitType', unitType || 'Unknown');
    sessionStorage.setItem('companyName', companyName || 'Unknown Company');
    sessionStorage.setItem('isPureVeg', isPureVeg ? 'true' : 'false');
    sessionStorage.setItem('userId', userId);

    const unsubscribe = fetchAssignedItems();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [unitCode, companyCode, userId]);

  const handleAvailabilityChange = async (itemId, isAvailable) => {
    try {
      console.log('MenuItems: Updating availability', { itemId, isAvailable, unitCode, companyCode, userId });
      const itemDocRef = doc(db, 'companymenu', itemId);
      await updateDoc(itemDocRef, { isAvailable });
      console.log('MenuItems: Availability updated successfully', { itemId, isAvailable, unitCode, companyCode });
    } catch (err) {
      console.error('MenuItems: Error updating availability', { err, itemId, unitCode, companyCode, userId });
      setError(`Failed to update availability for item ${itemId}: ${err.message}`);
    }
  };

  const handleBack = () => {
    console.log('MenuItems: Navigating to /kitchen_staff/home', { unitCode, companyCode, unitType, companyName, isPureVeg, userId });
    sessionStorage.setItem('unitCode', unitCode);
    sessionStorage.setItem('companyCode', companyCode);
    sessionStorage.setItem('unitType', unitType || 'Unknown');
    sessionStorage.setItem('companyName', companyName || 'Unknown Company');
    sessionStorage.setItem('isPureVeg', isPureVeg ? 'true' : 'false');
    sessionStorage.setItem('userId', userId);
    navigate(`/kitchen_staff/home?unit=${unitCode}&code=${companyCode}&type=${unitType || 'Unknown'}&name=${encodeURIComponent(companyName || 'Unknown Company')}&isPureVeg=${isPureVeg || false}`, {
      state: { unitCode, companyCode, unitType, companyName, isPureVeg, userId },
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading assigned menu items...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {companyName ? `${companyName} Assigned Menu Items` : 'Assigned Menu Items'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {menuItems.length > 0 ? (
        <List>
          {menuItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem sx={{ py: 2 }}>
                <Card
                  elevation={3}
                  sx={{
                    width: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'scale(1.02)' },
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      style={{ width: 100, height: 100, marginRight: 16, objectFit: 'cover', borderRadius: '4px' }}
                      onError={(e) => { e.target.src = '/placeholder.png'; }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {item.name} {item.isVeg && isPureVeg ? '(Veg)' : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Ingredients:</strong> {item.ingredients}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Description:</strong> {item.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mr: 2 }}>
                          <strong>Availability:</strong> {item.isAvailable ? 'Available' : 'Unavailable'}
                        </Typography>
                        <Switch
                          checked={item.isAvailable}
                          onChange={(e) => handleAvailabilityChange(item.id, e.target.checked)}
                          color="primary"
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </ListItem>
              {index < menuItems.length - 1 && <Box sx={{ mx: 2 }}><Divider /></Box>}
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No assigned menu items found for this {unitType} unit.
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button variant="contained" color="primary" onClick={handleBack}>
          Back to Home
        </Button>
      </Box>
    </Container>
  );
};

export default MenuItems;